import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getCourseBySlug, getCurriculum, getMyEnrollment } from '../lib/api';
import { formatMinutes, formatPrice } from '../lib/progress';
import { friendlyError } from '../lib/supabase';
import type { Chapter, Course, Enrollment, Lesson } from '../lib/types';
import { courseImage } from '../components/CourseCard';
import { Badge, Button, ButtonLink, Callout, MarkerList, Panel, SectionTitle, Spinner } from '../components/ui';
import { NotFound } from './misc';

export function CourseDetail() {
  const { slug = '' } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState<Course | null | undefined>(undefined);
  const [curr, setCurr] = useState<{ chapters: Chapter[]; lessons: Lesson[] } | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const c = await getCourseBySlug(slug);
        if (!live) return;
        setCourse(c);
        if (!c) return;
        const [cu, en] = await Promise.all([getCurriculum(c.id), user ? getMyEnrollment(c.id, user.id) : Promise.resolve(null)]);
        if (live) { setCurr(cu); setEnrollment(en); }
      } catch (e) { if (live) setError(friendlyError(e)); }
    })();
    return () => { live = false; };
  }, [slug, user]);

  if (error) return <div className="container page"><Callout variant="error">{error}</Callout></div>;
  if (course === undefined) return <Spinner />;
  if (course === null) return <NotFound message="That course isn't available." />;

  const lessonsByChapter = (id: string) => (curr?.lessons ?? []).filter((l) => l.chapter_id === id).sort((a, b) => a.position - b.position);

  return (
    <>
      <section className="title-band">
        <div className="container title-band-inner">
          <div>
            <SectionTitle overline={course.is_demo ? 'Demo course' : 'Online · self-paced'} subtitle={course.subtitle ?? undefined}>{course.title}</SectionTitle>
          </div>
        </div>
      </section>
      <div className="container page">
        <div className="two-col">
          <div className="main">
            {course.description && <p style={{ fontSize: 18, lineHeight: '28px', margin: 0 }}>{course.description}</p>}
            {course.objectives.length > 0 && (
              <section className="stack">
                <SectionTitle level={2}>What you'll learn</SectionTitle>
                <MarkerList items={course.objectives} />
              </section>
            )}
            <section className="stack">
              <SectionTitle level={2}>Curriculum</SectionTitle>
              {(curr?.chapters ?? []).sort((a, b) => a.position - b.position).map((ch) => (
                <details key={ch.id} className="box" style={{ padding: 0 }}>
                  <summary style={{ padding: '14px 20px', cursor: 'pointer', fontWeight: 600, color: 'var(--ink-strong)' }}>
                    {ch.title} <span className="small muted">· {lessonsByChapter(ch.id).length} lessons</span>
                  </summary>
                  <ul className="list-box" style={{ border: 0, borderTop: '1px solid var(--rule)', borderRadius: 0 }}>
                    {lessonsByChapter(ch.id).map((l) => (
                      <li key={l.id}>
                        <span style={{ flex: '1 1 240px' }}>{l.title}</span>
                        {l.is_preview && <Badge tone="brand">Free preview</Badge>}
                        {l.est_minutes ? <span className="small muted">{l.est_minutes} min</span> : null}
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
            </section>
          </div>
          <aside className="side">
            <img src={courseImage(course)} alt="" style={{ borderRadius: 'var(--radius-md)', aspectRatio: '16/9', objectFit: 'cover', width: '100%' }} />
            <Panel title="Course details">
              <div className="stack" style={{ gap: 10 }}>
                {course.duration_minutes ? <span>Duration: <strong>{formatMinutes(course.duration_minutes)}</strong></span> : null}
                {course.pdh ? <span>Professional development hours: <strong>{course.pdh} PDH</strong></span> : null}
                {course.certificate_enabled && <span>Certificate of completion</span>}
                <span className="price">{course.price_cents ? formatPrice(course.price_cents, course.currency) : 'Free'}</span>
                {enrollment ? (
                  <ButtonLink to={`/learn/${course.slug}`} iconAfter="chevronRight">Go to course</ButtonLink>
                ) : user ? (
                  <>
                    {/* Stripe Checkout is wired up in Phase 4; until then enrollments are created by an admin. */}
                    <Button variant="enroll" block disabled>Online enrollment opens at launch</Button>
                    <span className="small muted">Already purchased on Thinkific? Your access will carry over.</span>
                  </>
                ) : (
                  <ButtonLink to={`/sign-in?next=/courses/${course.slug}`} variant="enroll">Sign in to enroll</ButtonLink>
                )}
              </div>
            </Panel>
            <p className="small muted" style={{ margin: 0 }}>
              This course is educational and isn't legal advice or agency guidance. Professional development hours are awarded by
              BlueLine Environmental Institute and are not an accreditation. <Link to="/verify">Verify a certificate</Link>
            </p>
          </aside>
        </div>
      </div>
    </>
  );
}
